import { z } from 'zod';
import { localDateSchema, syncColumnsSchema } from './common.js';

/**
 * Entidades de nutricion (Fase 4).
 *
 * Alimentos y recetas se referencian por `slug` del catalogo en codigo, igual
 * que los ejercicios (ADR-024, extendido en ADR-036). Aqui solo viven los datos
 * del usuario: sus objetivos, su menu y lo que come de verdad. El peso diario
 * vive en `progress.ts`.
 *
 * Retirada el 21/09/2026 y recuperada el 28/09/2026 por decision del
 * propietario. Lo que cambio al volver: el objetivo energetico ya no sale de la
 * fase del bloque —que desde el 21/09 describe la ESTRUCTURA del entrenamiento,
 * no el balance energetico— sino de `nutritionGoalSchema`, que elige el usuario.
 */

// --------------------------------------------------------------------------
// Vocabulario persistido
// --------------------------------------------------------------------------

/**
 * Tipo de dia nutricional. Vive aqui, y no en el dominio, porque se persiste en
 * `nutrition_targets.day_type` y forma parte del CHECK de la tabla.
 *
 * Son los cuatro de docs/NUTRITION_ENGINE.md §4.2. El `strength_low` que
 * figuraba en DATA_MODEL.md no lo definia ninguna regla: un valor que nadie
 * sabe modular no es un tipo de dia, es una fila que no se puede calcular.
 */
export const dayTypeSchema = z.enum(['strength_high', 'strength', 'cardio', 'rest']);
export type DayType = z.infer<typeof dayTypeSchema>;

/**
 * Los huecos del dia que se persisten en `planned_meals.slot` y `meal_logs.slot`.
 *
 * `afternoon_snack` (la merienda) entro el 08/09/2026 con el esquema
 * configurable de comidas: hasta entonces el dia iba de comer a cenar sin nada
 * en medio, asi que un entrenamiento de tarde no tenia ninguna comida detras
 * salvo la cena.
 *
 * `extra` sigue en el enum y NO es una de las cinco franjas elegibles. Es un
 * valor **heredado**: existen `planned_meals` y `meal_logs` con ese slot de
 * antes de este cambio, y sacarlo del enum los dejaria sin validar al leer
 * —`normalizacion.ts` pasa toda fila por su schema— sin ganar nada. Ya no lo
 * genera nadie: ver `SELECTABLE_MEAL_SLOTS` y NUTRITION_ENGINE.md §4.3.
 */
export const mealSlotSchema = z.enum([
  'breakfast',
  'midmorning',
  'lunch',
  'afternoon_snack',
  'dinner',
  'extra',
]);
export type MealSlot = z.infer<typeof mealSlotSchema>;

/**
 * Orden real del dia. El enum no lo garantiza; esta lista si.
 *
 * `extra` conserva su sitio entre el almuerzo y la merienda para que una fila
 * antigua siga ordenandose donde estaba.
 */
export const MEAL_SLOT_ORDER: readonly MealSlot[] = [
  'breakfast',
  'midmorning',
  'lunch',
  'extra',
  'afternoon_snack',
  'dinner',
];

/**
 * Las cinco franjas entre las que el usuario elige sus comidas habituales.
 *
 * Estan aqui y no en el dominio por el mismo motivo que `MEAL_SLOT_ORDER`: se
 * PERSISTEN, en `user_preferences.meal_slots`, y el CHECK de la migracion 0027
 * repite esta lista. Una segunda copia en el dominio se separaria de la
 * columna sin que nada avisara.
 *
 * En orden del dia, que es tambien el orden en el que se pintan.
 */
export const SELECTABLE_MEAL_SLOTS: readonly MealSlot[] = [
  'breakfast',
  'midmorning',
  'lunch',
  'afternoon_snack',
  'dinner',
];

/** Cuantas comidas al dia se pueden elegir (requisito §5 del encargo). */
export const MEAL_COUNT_RANGE = { min: 1, max: 5 } as const;

/**
 * Las comidas de quien nunca ha configurado nada.
 *
 * Son las CUATRO que el motor generaba antes de que esto se pudiera elegir
 * (`slotsForDay`), no las cinco: para un usuario que ya tenia menu, el valor
 * por defecto tiene que dejar su dia como estaba. Un defecto que cambia el plan
 * de quien no ha pedido nada es un cambio silencioso de producto.
 */
export const DEFAULT_MEAL_SLOTS: readonly MealSlot[] = [
  'breakfast',
  'midmorning',
  'lunch',
  'dinner',
];

/**
 * Lo que el usuario quiere de su alimentacion. Lo ELIGE el, en la pantalla de
 * nutricion; nada lo deduce de una bascula, una cinta o la fase del bloque
 * (§6 del encargo del 23/09/2026: «sin imponer dietas o fases
 * automaticamente»). Y funciona en un solo sentido: el entrenamiento no lo lee.
 *
 * Son las cuatro fases que antes decidian la energia, con las mismas cifras
 * (`NUTRITION_GOAL_ENERGY` en `domain/nutrition/targets.ts`).
 */
export const nutritionGoalSchema = z.enum(['fat_loss', 'recomposition', 'maintenance', 'muscle_gain']);
export type NutritionGoal = z.infer<typeof nutritionGoalSchema>;

export const tdeeSourceSchema = z.enum(['formula', 'adaptive']);
export type TdeeSource = z.infer<typeof tdeeSourceSchema>;

/**
 * Un alimento con su cantidad dentro de una comida. La cantidad es **en crudo**
 * salvo que el alimento del catalogo diga lo contrario (§5.6).
 */
export const mealItemSchema = z.object({
  foodSlug: z.string().min(1).max(64),
  amount: z.number().min(0).max(5000),
  unit: z.enum(['g', 'ml', 'unit']),
});

export type MealItem = z.infer<typeof mealItemSchema>;

// --------------------------------------------------------------------------
// Objetivos
// --------------------------------------------------------------------------

export const nutritionTargetSchema = syncColumnsSchema.extend({
  /** Una fila por dia: el objetivo vigente ese dia queda congelado. */
  targetDate: localDateSchema,
  dayType: dayTypeSchema,
  kcal: z.number().int().min(800).max(8000),
  proteinG: z.number().min(0).max(600),
  carbsG: z.number().min(0).max(1200),
  fatG: z.number().min(0).max(400),
  tdeeEstimate: z.number().int().min(800).max(8000),
  tdeeSource: tdeeSourceSchema,
  /**
   * Gasto en reposo vigente al calcular el objetivo. Se guarda porque el suelo
   * calorico depende de el: sin este numero, el «¿Por que?» de manana no puede
   * explicar el recorte de hoy.
   */
  rmrEstimate: z.number().int().min(600).max(4000),
  /** Codigos que explican como se llego a estos numeros. */
  reasonCodes: z.array(z.string().min(1)).default([]),
  decisionId: z.uuid().nullable(),
});

export type NutritionTarget = z.infer<typeof nutritionTargetSchema>;

// --------------------------------------------------------------------------
// Menu
// --------------------------------------------------------------------------

export const mealPlanStatusSchema = z.enum(['draft', 'active', 'archived']);

export const mealPlanSchema = syncColumnsSchema.extend({
  /** Lunes de la semana planificada. */
  weekStart: localDateSchema,
  status: mealPlanStatusSchema,
  generatedBy: z.enum(['algorithm', 'claude', 'user']),
  varietyWindowDays: z.number().int().min(0).max(60),
});

export type MealPlan = z.infer<typeof mealPlanSchema>;

export const plannedMealStatusSchema = z.enum([
  'planned',
  'confirmed',
  'modified',
  'eating_out',
  'skipped',
  'logged',
]);

export type PlannedMealStatus = z.infer<typeof plannedMealStatusSchema>;

/**
 * Una receta generada, tal y como se guarda dentro de la comida.
 *
 * **Sin macros.** No es un olvido: los macros se calculan de los ingredientes
 * por Atwater (`itemsMacros`), igual que los del catalogo. Pedirselos al modelo
 * y comprobar despues que cuadran con la suma —lo que sugeria el §8— seria
 * validar su aritmetica, que no aporta nada cuando la suma la hacemos nosotros
 * y es la que manda. Lo que no se pregunta no puede venir mal.
 */
export const generatedRecipeSchema = z.object({
  nameEs: z.string().min(3).max(80),
  /**
   * Una frase que dice que es el plato. `null` en los snapshots anteriores al
   * 08/09/2026, que no la tenian: `.default(null)` la rellena al leer
   * (`normalizacion.ts`), y la pantalla no la pinta cuando falta.
   */
  description: z.string().min(10).max(200).nullable().default(null),
  minutes: z.number().int().min(1).max(180),
  /**
   * Texto corrido. Se conserva porque es lo que hay guardado en los snapshots
   * de antes de que existieran los pasos, y porque es lo que se muestra cuando
   * `steps` viene vacio. Para las recetas nuevas se compone de `steps`, asi que
   * no son dos versiones distintas de la misma elaboracion: es la misma.
   */
  instructions: z.string().min(20).max(1400),
  /**
   * La elaboracion en pasos ordenados y ejecutables (§25 del encargo).
   *
   * Vacio = snapshot antiguo, no receta sin elaboracion. Quien pinte esto tiene
   * que distinguirlo: `steps.length === 0` cae a `instructions`.
   */
  steps: z.array(z.string().min(10).max(300)).max(12).default([]),
  /** Alimentos y cantidades FINALES, ya en las unidades del catalogo. */
  items: z.array(mealItemSchema).min(1).max(12),
});

export type GeneratedRecipe = z.infer<typeof generatedRecipeSchema>;

export const plannedMealSchema = syncColumnsSchema.extend({
  mealPlanId: z.uuid(),
  mealDate: localDateSchema,
  slot: mealSlotSchema,
  /** Receta del catalogo en codigo. `null` si la comida es una suma de alimentos. */
  recipeSlug: z.string().min(1).max(64).nullable(),
  /** Alimentos y gramos FINALES, ya escalados. */
  items: z.array(mealItemSchema),
  kcal: z.number().min(0).max(6000),
  proteinG: z.number().min(0).max(500),
  carbsG: z.number().min(0).max(1000),
  fatG: z.number().min(0).max(400),
  status: plannedMealStatusSchema,
  /** Marca la tarjeta como «Ajustada» y activa el «¿Por que?». */
  adjustedByEngine: z.boolean(),
  adjustmentReason: z.string().max(400).nullable(),
  /**
   * Receta generada por la IA, ya validada contra el catalogo de alimentos.
   *
   * `null` en los dos casos normales: la comida sale del catalogo —y entonces
   * lo que hay es `recipeSlug`— o es una suma de alimentos sin plato detras.
   * Sin esto una receta generada se veria como una lista de gramos sin nombre
   * ni instrucciones, que es lo mismo que decir que no se veria como receta.
   */
  recipeSnapshot: generatedRecipeSchema.nullable().default(null),
});

export type PlannedMeal = z.infer<typeof plannedMealSchema>;

// --------------------------------------------------------------------------
// Registro
// --------------------------------------------------------------------------

export const entryModeSchema = z.enum(['confirmed', 'modified', 'manual', 'eating_out']);
export type EntryMode = z.infer<typeof entryModeSchema>;

export const mealLogSchema = syncColumnsSchema.extend({
  plannedMealId: z.uuid().nullable(),
  mealDate: localDateSchema,
  slot: mealSlotSchema,
  items: z.array(mealItemSchema),
  kcal: z.number().min(0).max(6000),
  proteinG: z.number().min(0).max(500),
  carbsG: z.number().min(0).max(1000),
  fatG: z.number().min(0).max(400),
  /** Nombre y receta tal y como estaban al registrar. */
  recipeSnapshot: z.record(z.string(), z.unknown()).nullable(),
  entryMode: entryModeSchema,
  loggedAt: z.iso.datetime({ offset: true }),
});

export type MealLog = z.infer<typeof mealLogSchema>;

// --------------------------------------------------------------------------
// Preferencias sobre recetas
// --------------------------------------------------------------------------

export const userRecipeSettingSchema = syncColumnsSchema.extend({
  recipeSlug: z.string().min(1).max(64),
  isFavorite: z.boolean(),
  rating: z.number().int().min(1).max(5).nullable(),
  /** Ultima vez que se planifico. Alimenta la ventana de variedad. */
  lastUsedOn: localDateSchema.nullable(),
});

export type UserRecipeSetting = z.infer<typeof userRecipeSettingSchema>;
